import React, { useState, useMemo } from 'react';
import {
  Users, Search, Plus, User, Mail, Phone, MapPin,
  Package, DollarSign, Star, Crown, Gem, Award,
  MoreVertical, Edit, Trash2, Eye, ChevronDown,
  Filter, RefreshCw, Building2, LayoutGrid, List,
  X, Calendar
} from 'lucide-react';

// Mock Data
const mockClients = [
  { 
    id: 'C001', boltId: 'BN-1001', fullName: 'Marcus Johnson', 
    email: 'marcus@email.com', phone: '+1 876 555-0101', parish: 'Kingston',
    address: '15 Hope Road', tier: 'gold', totalShipments: 45, totalSpent: 125000,
    createdAt: '2024-08-15', isVerified: true
  },
  { 
    id: 'C002', boltId: 'BN-1002', fullName: 'Sarah Williams', 
    email: 'sarah@business.com', phone: '+1 876 555-0102', parish: 'St. Andrew',
    address: '22 Constant Spring', tier: 'business_premium', totalShipments: 128, totalSpent: 450000,
    createdAt: '2024-03-22', isVerified: true
  },
  { 
    id: 'C003', boltId: 'BN-1003', fullName: 'James Brown', 
    email: 'james.b@gmail.com', phone: '+1 876 555-0103', parish: 'Portmore',
    address: '8 Caribbean Estate', tier: 'standard', totalShipments: 12, totalSpent: 28500,
    createdAt: '2024-11-01', isVerified: false
  },
  { 
    id: 'C004', boltId: 'BN-1004', fullName: 'Emily Davis', 
    email: 'emily@davis.co', phone: '+1 876 555-0104', parish: 'Kingston',
    address: '45 Red Hills Road', tier: 'diamond', totalShipments: 87, totalSpent: 285000,
    createdAt: '2024-05-10', isVerified: true
  },
  { 
    id: 'C005', boltId: 'BN-1005', fullName: 'Michael Chen', 
    email: 'mchen@trade.com', phone: '+1 876 555-0105', parish: 'Spanish Town',
    address: '12 Brunswick Ave', tier: 'business', totalShipments: 62, totalSpent: 198000,
    createdAt: '2024-06-28', isVerified: true
  },
  { 
    id: 'C006', boltId: 'BN-1006', fullName: 'Lisa Anderson', 
    email: 'lisa.a@yahoo.com', phone: '+1 876 555-0106', parish: 'Montego Bay',
    address: '88 Gloucester Ave', tier: 'gold', totalShipments: 34, totalSpent: 95000,
    createdAt: '2024-09-05', isVerified: true
  },
  { 
    id: 'C007', boltId: 'BN-1007', fullName: 'Robert Taylor', 
    email: 'rtaylor@corp.com', phone: '+1 876 555-0107', parish: 'Kingston',
    address: '3 Oxford Road', tier: 'enterprise', totalShipments: 245, totalSpent: 1250000,
    createdAt: '2023-12-01', isVerified: true
  },
  { 
    id: 'C008', boltId: 'BN-1008', fullName: 'Amanda White', 
    email: 'amanda.w@mail.com', phone: '+1 876 555-0108', parish: 'Mandeville',
    address: '55 Ward Ave', tier: 'standard', totalShipments: 8, totalSpent: 18500,
    createdAt: '2024-12-01', isVerified: false
  },
];

const TIER_CONFIG = {
  free: { label: 'Free', icon: User, color: 'tier-free', discount: 0 },
  standard: { label: 'Standard', icon: User, color: 'tier-standard', discount: 0 },
  gold: { label: 'Gold', icon: Star, color: 'tier-gold', discount: 2 },
  diamond: { label: 'Diamond', icon: Gem, color: 'tier-diamond', discount: 3 },
  business: { label: 'Business', icon: Crown, color: 'tier-business', discount: 4 },
  business_premium: { label: 'Business+', icon: Crown, color: 'tier-business-premium', discount: 5 },
  business_platinum: { label: 'Platinum', icon: Crown, color: 'tier-platinum', discount: 10 },
  enterprise: { label: 'Enterprise', icon: Building2, color: 'tier-enterprise', discount: 15 },
  business_elite: { label: 'Elite', icon: Award, color: 'tier-elite', discount: 30 },
  executive: { label: 'Executive', icon: Award, color: 'tier-executive', discount: 40 },
};

const formatJMD = (amount) => `J$${(amount || 0).toLocaleString()}`;

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, colorClass }) => (
  <div className={`clients-stat-card ${colorClass || ''}`} data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="stat-content">
      <span className="stat-title">{title}</span>
      <span className="stat-value">{value}</span>
    </div>
    <div className="stat-icon-wrapper">
      <Icon size={20} />
    </div>
  </div>
);

// Client Card Component (Grid View)
const ClientCard = ({ client, onView, onEdit, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const tierConfig = TIER_CONFIG[client.tier] || TIER_CONFIG.standard;
  const TierIcon = tierConfig.icon;

  return (
    <div className="client-card" onClick={() => onView(client)} data-testid={`client-${client.id}`}>
      <div className="client-card-header">
        <div className="client-avatar">
          {client.fullName?.charAt(0)?.toUpperCase() || 'C'}
        </div>
        <div className="client-info">
          <span className="client-name">{client.fullName}</span>
          <span className="client-id">{client.boltId}</span>
        </div>
        <div className="client-menu">
          <button className="menu-btn" onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}>
            <MoreVertical size={16} />
          </button>
          {showMenu && (
            <div className="menu-dropdown">
              <button onClick={(e) => { e.stopPropagation(); onView(client); setShowMenu(false); }}>
                <Eye size={14} /> View Details
              </button>
              <button onClick={(e) => { e.stopPropagation(); onEdit(client); setShowMenu(false); }}>
                <Edit size={14} /> Edit
              </button>
              <button className="delete-action" onClick={(e) => { e.stopPropagation(); onDelete(client); setShowMenu(false); }}>
                <Trash2 size={14} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="client-contact">
        {client.email && (
          <div className="contact-item">
            <Mail size={14} />
            <span>{client.email}</span>
          </div>
        )}
        {client.phone && (
          <div className="contact-item">
            <Phone size={14} />
            <span>{client.phone}</span>
          </div>
        )}
        {client.parish && (
          <div className="contact-item">
            <MapPin size={14} />
            <span>{client.parish}</span>
          </div>
        )}
      </div>

      <div className="client-footer">
        <span className={`tier-badge ${tierConfig.color}`}>
          <TierIcon size={12} />
          {tierConfig.label}
        </span>
        {client.totalShipments > 0 && (
          <span className="shipment-count">
            <Package size={12} />
            {client.totalShipments} shipments
          </span>
        )}
      </div>
    </div>
  );
};

// Client List Row Component (List View)
const ClientListRow = ({ client, onView, onEdit, onDelete }) => {
  const [showMenu, setShowMenu] = useState(false);
  const tierConfig = TIER_CONFIG[client.tier] || TIER_CONFIG.standard;
  const TierIcon = tierConfig.icon;

  return (
    <div className="client-list-row" onClick={() => onView(client)}>
      <div className="client-avatar small">
        {client.fullName?.charAt(0)?.toUpperCase() || 'C'}
      </div>
      <div className="client-name-col">
        <span className="client-name">{client.fullName}</span>
        <span className="client-id">{client.boltId}</span>
      </div>
      <div className="client-email-col">{client.email || '-'}</div>
      <div className="client-phone-col">{client.phone || '-'}</div>
      <div className="client-parish-col">{client.parish || '-'}</div>
      <span className={`tier-badge ${tierConfig.color}`}>
        <TierIcon size={12} />
        <span className="tier-label">{tierConfig.label}</span>
      </span>
      <div className="client-menu">
        <button className="menu-btn" onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}>
          <MoreVertical size={16} />
        </button>
        {showMenu && (
          <div className="menu-dropdown">
            <button onClick={(e) => { e.stopPropagation(); onView(client); setShowMenu(false); }}>
              <Eye size={14} /> View
            </button>
            <button onClick={(e) => { e.stopPropagation(); onEdit(client); setShowMenu(false); }}>
              <Edit size={14} /> Edit
            </button>
            <button className="delete-action" onClick={(e) => { e.stopPropagation(); onDelete(client); setShowMenu(false); }}>
              <Trash2 size={14} /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Client Detail Modal
const ClientDetailModal = ({ client, onClose }) => {
  if (!client) return null;
  
  const tierConfig = TIER_CONFIG[client.tier] || TIER_CONFIG.standard;
  const TierIcon = tierConfig.icon;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content client-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Client Details</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="dialog-body">
          <div className="client-profile-section">
            <div className="profile-avatar large">
              {client.fullName?.charAt(0)?.toUpperCase() || 'C'}
            </div>
            <div className="profile-info">
              <h3>{client.fullName}</h3>
              <span className="profile-id">{client.boltId}</span>
              <div className="profile-badges">
                <span className={`tier-badge ${tierConfig.color}`}>
                  <TierIcon size={12} />
                  {tierConfig.label}
                </span>
                {client.isVerified ? (
                  <span className="verified-badge">Verified</span>
                ) : (
                  <span className="unverified-badge">Unverified</span>
                )}
              </div>
            </div>
          </div>

          <div className="client-details-grid">
            <div className="detail-item">
              <Mail size={16} />
              <div>
                <span className="label">Email</span>
                <span className="value">{client.email}</span>
              </div>
            </div>
            <div className="detail-item">
              <Phone size={16} />
              <div>
                <span className="label">Phone</span>
                <span className="value">{client.phone || '-'}</span>
              </div>
            </div>
            <div className="detail-item">
              <MapPin size={16} />
              <div>
                <span className="label">Address</span>
                <span className="value">{client.address}, {client.parish}</span>
              </div>
            </div>
            <div className="detail-item">
              <Calendar size={16} />
              <div>
                <span className="label">Member Since</span>
                <span className="value">{client.createdAt}</span>
              </div>
            </div>
          </div>

          <div className="client-stats-section">
            <div className="mini-stat">
              <Package size={18} />
              <div>
                <span className="value">{client.totalShipments}</span>
                <span className="label">Shipments</span>
              </div>
            </div>
            <div className="mini-stat">
              <DollarSign size={18} />
              <div>
                <span className="value">{formatJMD(client.totalSpent)}</span>
                <span className="label">Total Spent</span>
              </div>
            </div>
          </div>
        </div>

        <div className="dialog-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
          <button className="btn-primary">
            <Package size={16} />
            Create Shipment
          </button>
        </div>
      </div>
    </div>
  );
};

// Create/Edit Client Dialog
const ClientFormDialog = ({ client, onClose, onSave }) => {
  const [form, setForm] = useState({
    fullName: client?.fullName || '',
    email: client?.email || '',
    phone: client?.phone || '',
    parish: client?.parish || '',
    address: client?.address || '',
    tier: client?.tier || 'standard'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.fullName.trim()) {
      alert('Name is required');
      return;
    }
    onSave(form);
    onClose();
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content client-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{client ? 'Edit Client' : 'Add Client'}</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="dialog-body">
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                placeholder="John Doe"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="john@email.com"
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 876 XXX XXXX"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Parish</label>
              <input
                type="text"
                value={form.parish}
                onChange={(e) => setForm({ ...form, parish: e.target.value })}
                placeholder="Kingston"
              />
            </div>

            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Street address"
              />
            </div>

            <div className="form-group">
              <label>Tier</label>
              <select
                value={form.tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}
              >
                {Object.entries(TIER_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="dialog-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary">
              {client ? 'Update Client' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function TenantClients() {
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Filter clients
  const filteredClients = useMemo(() => {
    return mockClients.filter(client => {
      const matchesSearch = !searchQuery ||
        client.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.boltId?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTier = tierFilter === 'all' || client.tier === tierFilter;
      return matchesSearch && matchesTier;
    });
  }, [searchQuery, tierFilter]);

  // Stats
  const stats = useMemo(() => ({
    total: mockClients.length,
    business: mockClients.filter(c => c.tier?.startsWith('business') || c.tier === 'enterprise').length,
    gold: mockClients.filter(c => c.tier === 'gold').length,
    diamond: mockClients.filter(c => c.tier === 'diamond').length,
  }), []);

  const handleSaveClient = (formData) => {
    console.log('Saving client:', formData);
    alert(`Client ${editingClient ? 'updated' : 'created'} successfully!`);
    setEditingClient(null);
  };

  const handleDeleteClient = (client) => {
    console.log('Deleting client:', client.id);
    alert(`Client ${client.fullName} deleted`);
    setShowDeleteConfirm(null);
  };

  return (
    <div className="clients-page" data-testid="clients-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-icon">
            <Users size={28} />
          </div>
          <div className="header-text">
            <h1>Clients</h1>
            <p>Customer directory and management</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary">
            <RefreshCw size={16} />
          </button>
          <button className="btn-primary" onClick={() => setShowCreateDialog(true)}>
            <Plus size={16} />
            <span>Add Client</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="clients-stats-grid">
        <StatCard title="Total Clients" value={stats.total} icon={Users} />
        <StatCard title="Business Tier" value={stats.business} icon={Building2} colorClass="stat-purple" />
        <StatCard title="Gold Members" value={stats.gold} icon={Star} colorClass="stat-yellow" />
        <StatCard title="Diamond Members" value={stats.diamond} icon={Gem} colorClass="stat-blue" />
      </div>

      {/* Main Content */}
      <div className="clients-content">
        {/* Toolbar */}
        <div className="clients-toolbar">
          <div className="search-input">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search by name, email, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="toolbar-right">
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="tier-filter"
            >
              <option value="all">All Tiers</option>
              {Object.entries(TIER_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>

            <div className="view-toggle">
              <button
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <LayoutGrid size={16} />
              </button>
              <button
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <List size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Client List */}
        <div className="clients-list-wrapper">
          {filteredClients.length === 0 ? (
            <div className="empty-state">
              <Users size={48} />
              <h3>No clients found</h3>
              <p>{searchQuery ? 'Try a different search term' : 'Add your first client to get started'}</p>
              <button className="btn-primary" onClick={() => setShowCreateDialog(true)}>
                <Plus size={16} />
                Add Client
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="clients-grid">
              {filteredClients.map(client => (
                <ClientCard
                  key={client.id}
                  client={client}
                  onView={setSelectedClient}
                  onEdit={setEditingClient}
                  onDelete={setShowDeleteConfirm}
                />
              ))}
            </div>
          ) : (
            <div className="clients-list">
              <div className="list-header">
                <div className="col-avatar"></div>
                <div className="col-name">Name</div>
                <div className="col-email">Email</div>
                <div className="col-phone">Phone</div>
                <div className="col-parish">Parish</div>
                <div className="col-tier">Tier</div>
                <div className="col-actions"></div>
              </div>
              {filteredClients.map(client => (
                <ClientListRow
                  key={client.id}
                  client={client}
                  onView={setSelectedClient}
                  onEdit={setEditingClient}
                  onDelete={setShowDeleteConfirm}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Client Detail Modal */}
      {selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
        />
      )}

      {/* Create/Edit Dialog */}
      {(showCreateDialog || editingClient) && (
        <ClientFormDialog
          client={editingClient}
          onClose={() => { setShowCreateDialog(false); setEditingClient(null); }}
          onSave={handleSaveClient}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="dialog-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="dialog-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>Delete Client</h2>
              <button className="close-btn" onClick={() => setShowDeleteConfirm(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="dialog-body">
              <p>Are you sure you want to delete <strong>{showDeleteConfirm.fullName}</strong>? This action cannot be undone.</p>
            </div>
            <div className="dialog-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => handleDeleteClient(showDeleteConfirm)}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
